import React, { useEffect, useState, useCallback } from "react";
import {
  handleExpiredSession,
  startSessionMonitoring,
  trackUserActivity,
} from "../utils/cookies";

interface SessionMonitorProps {
  onSessionExpiry?: () => void;
  onSessionWarning?: (minutesLeft: number) => void;
  showNotifications?: boolean;
}

interface SessionNotificationProps {
  type: "warning" | "expired";
  minutesLeft?: number;
  onExtend?: () => void;
  onDismiss?: () => void;
}

const SessionNotification: React.FC<SessionNotificationProps> = ({
  type,
  minutesLeft = 0,
  onExtend,
  onDismiss,
}) => {
  if (type === "warning") {
    return (
      <div className="fixed top-4 right-4 z-50 bg-amber-100 border-l-4 border-amber-500 p-4 rounded shadow-lg max-w-sm">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-amber-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-amber-800">
              Session Expiring Soon
            </p>
            <p className="mt-1 text-sm text-amber-700">
              Your session will expire in {Math.round(minutesLeft)} minutes due
              to inactivity. Your customer details and measurements will be
              saved, but you'll need to reselect your booking time.
            </p>
            <div className="mt-3 flex space-x-2">
              {onExtend && (
                <button
                  onClick={onExtend}
                  className="bg-amber-500 text-white px-3 py-1 text-xs rounded hover:bg-amber-600 transition-colors"
                >
                  Stay Active
                </button>
              )}
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="text-amber-700 px-3 py-1 text-xs rounded border border-amber-300 hover:bg-amber-50 transition-colors"
                >
                  Dismiss
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === "expired") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg
                className="h-8 w-8 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">
              Session Expired
            </h3>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">
              Your session has expired due to inactivity. Don't worry - we've
              saved your customer details and measurements!
            </p>

            <div className="bg-green-50 border border-green-200 rounded p-3">
              <p className="text-sm text-green-800">
                <strong>Preserved data:</strong>
              </p>
              <ul className="text-sm text-green-700 mt-1 ml-4 list-disc">
                <li>Customer contact information</li>
                <li>Glass measurements</li>
                <li>Job description and notes</li>
                <li>Service type and pricing</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-3">
              <p className="text-sm text-amber-800">
                <strong>You'll need to reselect:</strong>
              </p>
              <ul className="text-sm text-amber-700 mt-1 ml-4 list-disc">
                <li>Booking date and time</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onDismiss}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
            >
              Continue Booking
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const SessionMonitor: React.FC<SessionMonitorProps> = ({
  onSessionExpiry,
  onSessionWarning,
  showNotifications = true,
}) => {
  const [notification, setNotification] = useState<{
    type: "warning" | "expired";
    minutesLeft?: number;
  } | null>(null);

  const handleWarning = useCallback(
    (minutesLeft: number) => {
      if (showNotifications) {
        setNotification({ type: "warning", minutesLeft });
      }
      onSessionWarning?.(minutesLeft);
    },
    [onSessionWarning, showNotifications]
  );

  const handleExpiry = useCallback(async () => {
    console.log("🔄 Session expired - handling cleanup");

    // Handle the expired session (preserves customer data, clears booking selections)
    await handleExpiredSession();

    if (showNotifications) {
      setNotification({ type: "expired" });
    }

    onSessionExpiry?.();
  }, [onSessionExpiry, showNotifications]);

  const handleExtendSession = useCallback(async () => {
    console.log("⏰ Extending session via user activity");

    // Track user activity to extend session
    await trackUserActivity("session_extended", "session_monitor", {
      action: "user_requested_extension",
    });

    // Clear warning notification
    setNotification(null);
  }, []);

  const handleDismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const handleDismissExpiry = useCallback(() => {
    setNotification(null);
    // Optionally redirect to a specific page or refresh
    window.location.reload();
  }, []);

  // Start monitoring when component mounts
  useEffect(() => {
    console.log("🔍 Starting session monitoring...");

    const stopMonitoring = startSessionMonitoring(handleWarning, handleExpiry);

    // Add heartbeat - send activity every 5 minutes to keep active sessions alive
    const heartbeatInterval = setInterval(() => {
      trackUserActivity("session_heartbeat", "session_monitor", {
        action: "automatic_keepalive",
      });
    }, 5 * 60 * 1000); // Every 5 minutes

    // Cleanup function
    return () => {
      console.log("🛑 Stopping session monitoring");
      stopMonitoring();
      clearInterval(heartbeatInterval);
    };
  }, [handleWarning, handleExpiry]);

  // Track user activity for various interactions
  useEffect(() => {
    const events = ["click", "scroll", "keypress", "mousemove"];
    const throttledTracking = throttle((event: string) => {
      trackUserActivity(`user_${event}`, "session_monitor");
    }, 15000); // Reduced throttle to 15 seconds (from 30s) to capture more activity

    const handlers = events.map((event) => {
      const handler = () => throttledTracking(event);
      document.addEventListener(event, handler, { passive: true });
      return { event, handler };
    });

    return () => {
      handlers.forEach(({ event, handler }) => {
        document.removeEventListener(event, handler);
      });
    };
  }, []);

  if (!notification) {
    return null;
  }

  return (
    <SessionNotification
      type={notification.type}
      minutesLeft={notification.minutesLeft}
      onExtend={
        notification.type === "warning" ? handleExtendSession : undefined
      }
      onDismiss={
        notification.type === "warning"
          ? handleDismissNotification
          : handleDismissExpiry
      }
    />
  );
};

// Throttle function to limit how often we track activity
function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastExecTime = 0;

  return ((...args: Parameters<T>) => {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func(...args);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  }) as T;
}

export default SessionMonitor;
